import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * useState whose value survives leaving the page and coming back to it.
 *
 * List screens keep their filters and pagination in component state, which used
 * to be safe because a row opened in a new tab and the list never unmounted.
 * Now that rows navigate in place, going Back remounts the list with empty
 * filters unless the value was parked somewhere. It is parked per path, in
 * sessionStorage, so it is scoped to the tab and disappears when the tab does.
 */

const PREFIX = "erp:page-state:";

const read = <T,>(storageKey: string, fallback: T): T => {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch (error) {
    return fallback;
  }
};

const write = (storageKey: string, value: unknown) => {
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(value));
  } catch (error) {
    /* a full or unavailable sessionStorage must not break the page */
  }
};

export function useRestorableState<T>(
  name: string,
  initialValue: T
): [T, (value: T | ((previous: T) => T)) => void] {
  const { pathname } = useLocation();
  const storageKey = `${PREFIX}${pathname}:${name}`;
  const initialRef = useRef(initialValue);

  const [state, setState] = useState<{ key: string; value: T }>(() => ({
    key: storageKey,
    value: read(storageKey, initialValue),
  }));

  // The path can change while the component stays mounted (another document
  // category, another tab). That is a different page as far as the stored
  // state goes, so re-read instead of writing this page's value under its key.
  if (state.key !== storageKey) {
    setState({ key: storageKey, value: read(storageKey, initialRef.current) });
  }

  useEffect(() => {
    if (state.key !== storageKey) return;
    write(storageKey, state.value);
  }, [storageKey, state]);

  const setValue = useCallback((value: T | ((previous: T) => T)) => {
    setState((previous) => {
      const next =
        typeof value === "function"
          ? (value as (previous: T) => T)(previous.value)
          : value;
      // Same bail-out useState gives, which callers rely on to avoid loops.
      return next === previous.value ? previous : { key: previous.key, value: next };
    });
  }, []);

  return [state.value, setValue];
}

export default useRestorableState;
